<template>
	<div :class="dropDownClasses" :disabled="disabled">
		<Label
			class="dropdown__label"
			:labelText="labelText"
			:forElementId="uid"
			v-if="labelText"
		>
			<template v-slot:leading>
				<slot name="beforeLabel"> </slot>
			</template>

			<template v-slot:trailing>
				<slot name="afterLabel"> </slot>
			</template>
		</Label>
		<div
			:class="['dropdown__body', expanded && 'is-open']"
			:style="dropDownStyle"
			ref="popperReference"
			tabindex="0"
			@blur="onBlurDropDown"
		>
			<div
				class="dropdown__btn"
				@click="onClick"
				data-testid="dropdown-btn"
			>
				<input
					class="dropdown__input"
					tabindex="0"
					name="search"
					type="text"
					:aria-label="ariaLabel"
					v-model="searchTextData"
					:placeholder="placeholder"
					@input="onSearchInput"
					:hasFocus="expanded"
					autocomplete="off"
					v-if="searchable"
					:style="inputStyle"
					data-testid="dropdown-input"
				/>

				<span
					class="dropdown__text"
					v-if="!searchable && selectedText !== ''"
				>
					{{ selectedText }}
				</span>
				<span
					class="dropdown__text is-placeholder"
					v-if="!searchable && !selectedText"
				>
					{{ placeholder }}
				</span>
			</div>
			<span
					class="dropdown__warning-icon"
					v-if="hasIconWarning && hasWarning"
					ref="warningIcon"
					@mouseover="warningTooltipVisible = true"
					@focus="warningTooltipVisible = true"
					@mouseout="warningTooltipVisible = false"
					@blur="warningTooltipVisible = false"
					tabindex="0"
				>
					<i class="icon icon-warning-01"></i>
				</span>
			<span
				:class="['dropdown__clear-btn', withFocus && 'on-focus']"
				@blur="withFocus = false"
				v-if="isClearFeatureAvailable"
				@click="onClearSearch"
			>
				<ButtonIcon iconClassName="icon-remove-2" />
			</span>
			<span class="dropdown__icon" @click="onClick" ref="button">
				<i class="icon icon-arrow_down_filled"></i>
			</span>
		</div>
		<Tooltip
			v-if="hasIconWarning"
			:placement="tooltipPlacement"
			:visible="warningTooltipVisible"
			trackedRefName="popperReference"
			positionStrategy="fixed"
			:offset="[0, 8]"
			state="warning"
		>
			<template #default>
				<ul
					class="input__icon-warning-feedback"
					v-if="warningMessages.length"
				>
					<li
						v-for="(message, index) in warningMessages"
						:key="index"
					>
						{{ message }}
					</li>
				</ul>
			</template>
		</Tooltip>
		<PopperPosition
			trackedRefName="popperReference"
			popoverRefName="menu"
			placement="bottom-start"
			:popperVisible="expanded"
			:strategy="strategy"
			@popperMinWidthChanged="setpopperMinWidth"
		/>
		<InfiniteScroller
			@scrollBoundary="getNextPage"
			:hasMoreResults="hasMoreSearchListOptions"
			:loading="loading"
			:wheelElement="menuContainer"
			:scrollElement="menuContainer"
		>
			<template v-slot:scrollDiv>
				<Menu
					:id="`${uid}_ddl`"
					:options="optionList"
					v-model="selectedOption"
					@change="onSelect"
					:visible="expanded"
					:minWidth="popperMinWidth"
					ref="menu"
					@mounted="menuMounted"
				/>
			</template>
		</InfiniteScroller>
		<HelpFeedback
			class="dropdown__help-feedback"
			:style="dropDownStyle"
			:helpText="helpText"
			v-if="hasHelp"
		/>
		<ErrorFeedback
			class="dropdown__error-feedback"
			:errorMessages="errorMessages"
			v-if="hasError"
		/>
		<WarningFeedback
			class="dropdown__warning-feedback"
			:warningMessages="warningMessages"
			v-if="hasWarning && !hasIconWarning"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import ErrorFeedback from '@/components/forms/shared-stateless-elements/error-feedback.vue';
import WarningFeedback from '@/components/forms/shared-stateless-elements/warning-feedback.vue';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import baseDropdownMixin from '@/mixins/base-dropdown-mixin';
import Menu from '@/components/forms/menu/menu.vue';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/dropdown-variant-classes';
import PopperPosition from '@/components/visual/popper-position/popper-position.vue';
import formComponentMixin from '@/mixins/form-component-mixin';
import menuListMixin from '@/mixins/menu-list-mixin';
import InfiniteScroller from '@/components/shells/infinite-scroller/infinite-scroller.vue';
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';

export default {
	name: 'Dropdown',
	mixins: [
		baseComponentMixin,
		componentVariantMixin,
		baseDropdownMixin,
		formComponentMixin,
		menuListMixin
	],
	components: {
		Label,
		Menu,
		ErrorFeedback,
		WarningFeedback,
		HelpFeedback,
		PopperPosition,
		InfiniteScroller,
		ButtonIcon,
		Tooltip
	},
	data() {
		return {
			contentLoading: false,
			InfiniteScrollElement: undefined,
			textSearching: false,
			refField: undefined,
			warningTooltipVisible: false
		};
	},
	props: {
		strategy: {
			type: String,
			default: 'absolute'
		},
		hasIconWarning: {
			type: Boolean,
			default: false
		},
		tooltipPlacement: {
			type: String,
			default: 'auto'
		}
	},
	created() {
		this.variantClasses = variantClasses;
	},
	mounted() {
		this.$nextTick(function() {
			this.refField = this.$refs.popperReference;

			document.addEventListener('scroll', this.handleScroll, true);
		});
	},
	beforeDestroy() {
		document.removeEventListener('scroll', this.handleScroll, true);
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				this.selected = val;
				this.searchTextData = this.selectedText;
			}
		}
	},
	model: {
		prop: 'value',
		event: 'change'
	},
	methods: {
		onSelect(option) {
			this.selectedOption = option;
			this.searchTextData = this.selectedText;
			this.$emit('input', this.selected);
			this.$emit('change', this.selected);
			this.textSearching = false;
			this.expanded = false;
		},
		onBlurDropDown() {
			this.$emit('blur', this.selected);
		},
		menuMounted(menuRef) {
			if (menuRef) this.InfiniteScrollElement = menuRef.menuContainer;
		},
		getNextPage() {
			this.$emit('getNextPage');
		},
		onSearchInput(e) {
			this.searchTextData = e.target.value;
			this.textSearching = true;
			this.expanded = true;
			this.$emit('search', this.searchTextData);
		},
		triggerValidation() {
			this.validate(this.selected, undefined);
		},
		handleScroll(event) {
			const menuElement = this.$refs.menu ? this.$refs.menu.$el : null;
			const popperElement = this.$refs.popperReference;

			const isInsideDropdown =
				(menuElement && menuElement.contains(event.target)) ||
				(popperElement && popperElement.contains(event.target));

			if (!isInsideDropdown) {
				this.expanded = false;
				if (popperElement) {
					popperElement.blur();
				}
			}
		}
	},
	computed: {
		optionList() {
			const matches = this.matchOptions;
			if (this.textSearching) {
				return matches;
			}
			return this.options;
		},
		selectedOption: {
			get: function() {
				let opt = {};

				if (this.options && this.options.length > 0) {
					opt = this.options.find(o => o.value === this.selected);
				}

				return opt;
			},
			set: function(option) {
				this.selected = option ? option.value : null;
			}
		},
		selectedText() {
			const opt = this.selectedOption;
			let text = '';

			if (opt) {
				text = opt.text;
			}

			return text;
		},
		dropDownStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		},
		dropDownClasses() {
			return [
				'dropdown', // default class
				...this.variantClassList,
				this.hasError && 'has-error',
				this.hasWarning && 'has-warning',
				this.disabled && 'is-disabled',
				this.selectedText && 'is-selected',
				this.isClearFeatureAvailable && 'has-clear-feature'
			];
		},
		menuContainer() {
			return this.InfiniteScrollElement;
		},
		inputStyle() {
			if (this.searchable) {
				return {
					width: '100%'
				};
			} else {
				return {};
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');
@include dropdown-styles('dropdown');
@include left-label('dropdown');

:deep(.btn-icon) {
	&:hover {
		background-color: var(--bg-brand-2-tint);
		color: var(--bg-brand-2-tint-fg-2);

		.icon:after {
			border: none;
		}
	}
	&:focus,
	&:active {
		outline: $focus-width $focus-style var(--brand-2);
		outline-offset: $focus-offset;

		.icon:after {
			border: none;
		}
	}
}

:deep(mark) {
	background: var(--bg-brand-2-tint);
	color: var(--bg-brand-2-tint-fg-2);
}

:deep(.input) {
	.input {
		&__field-container {
			.input__field,
			&::after {
				border: none;
				height: calc(100% - 1px);
				padding: 0;
			}
		}
	}
}

.dropdown__warning-icon {
	color: var(--ui-warning);
	align-items: center;
	padding: $field-padding-y $field-padding-x;
}
</style>
